import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useAssociatedMentor } from '../../hooks/apiHooks/MentorHooks'
import { useMentorsState } from '../../hooks/storeHooks/useMentors'
import useOnScreen from '../../hooks/useOnScreen'
import { IContext, IContextGroup } from '../../types/contextGroup'
import { analytics } from '../../utils/analytics'
import { getTimeAgo } from '../../utils/getTimeAgo'
import { CaseIcon } from '../Icons'
import { ItemSwitcher } from '../ItemSwitcher/ItemSwitcher'
import { PlayButton } from '../PlayButton'
import { SmoothImage } from '../SmoothImage'
import { SmoothVideo } from '../SmoothVideo'

interface IRecentCardProps {
  contextsGroup: IContextGroup
}

function capitalizeFirstLetter(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const RecentCard: React.FC<IRecentCardProps> = memo(({ contextsGroup }) => {
  const [currentContext, setCurrentContext] = useState<IContext | null>()
  const [currentItemIndex, setCurrentItemIndex] = useState(0)
  const { data, isFetching } = useAssociatedMentor(currentContext?.id)
  const cardRef = useRef<HTMLDivElement | null>(null)
  const { setSelectedMentor } = useMentorsState()
  const navigate = useNavigate()

  const isOnScreen = useOnScreen(cardRef, [], `${window.innerHeight}px 0px`)

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    if (isFetching) {
      e.preventDefault()
      return
    }
    setSelectedMentor(data || null)
  }

  const handleCardClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    handleClick(e)
    analytics.logEvent('watch_session_clicked_query', {
      query: contextsGroup?.userQuery,
    })
  }

  const handleCompanionClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    handleClick(e)
    if (data?.slug) {
      analytics.logEvent('watch_session_clicked_companion', {
        companion_slug: data?.slug,
      })
    }
  }

  useEffect(() => {
    if (contextsGroup?.contexts) {
      setCurrentContext(contextsGroup.contexts[currentItemIndex])
    }
  }, [contextsGroup, currentItemIndex])

  const onChange = (val: number) => {
    setCurrentItemIndex(prev => {
      if (prev + val >= contextsGroup.contexts.length) {
        return 0
      }
      if (prev + val < 0) {
        return contextsGroup.contexts.length - 1
      }
      return prev + val
    })
  }

  const timeAgo = getTimeAgo(contextsGroup?.createdAt)

  const navigateToSessionPage = (e: React.MouseEvent<HTMLDivElement>) => {
    handleCardClick(e as unknown as React.MouseEvent<HTMLAnchorElement>)
    navigate(`/${data?.slug}?start=true&context=${currentContext?.id}`, {
      state: { prevLocation: `${window.location.pathname}` },
    })
  }

  const mentorIsReady = useMemo(
    () =>
      data?.status === 'Completed' ||
      data?.status === 'ReadyToUse' ||
      data?.status === 'GenerationCompleted' ||
      !isFetching,
    [data?.mentorId, isFetching],
  )

  const handleQueryClick = () => {
    analytics.logEvent('watch_session_clicked', {
      source: 'recent',
    })
  }

  return (
    <div
      className="tc-mv-card-recent rounded-regular"
      ref={cardRef}
      data-testid="recent-card"
    >
      <div className="tc-mv-card-title tc-mv-card-item ">
        <div
          className="absolute flex items-center justify-between w-full top-4 px-4 z-10 gap-2"
          data-testid="recent-list-container"
        >
          <Link
            to={`/search?query=${encodeURIComponent(contextsGroup?.userQuery)}`}
            onClick={handleQueryClick}
            data-testid="card-title-link"
          >
            <h2
              className="text-white leading-[150%] tc-mv-two-lines-text"
              title={contextsGroup?.userQuery}
            >
              {contextsGroup?.userQuery &&
                capitalizeFirstLetter(contextsGroup?.userQuery)}
            </h2>
          </Link>
          <div className="flex-grow-0 flex-shrink-0 self-start">
            <ItemSwitcher
              currentItem={currentItemIndex + 1}
              itemsCount={contextsGroup?.contexts.length}
              onChange={onChange}
              isLooped
              disabled={false}
            />
          </div>
        </div>
        <Link
          to={`/${data?.slug}?start=true&context=${currentContext?.id}`}
          state={{ prevLocation: `${window.location.pathname}` }}
          onClick={handleCardClick}
          className={`${!currentContext?.id ? 'pointer-events-none' : ''}`}
          data-testid="watch-session-link"
        >
          <div className="absolute z-20 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 tc-mv-play-button">
            <PlayButton />
          </div>
          <div className="tc-mv-card-bg-img ">
            <SmoothImage
              src={currentContext?.imageUrl}
              alt={currentContext?.title}
              className="object-cover w-full h-full"
              isFetching={isFetching}
              dataTestid="card-image"
            />
          </div>
        </Link>
      </div>
      <div
        className="tc-mv-avatar-details flex content-center cursor-pointer min-h-[150px]"
        onClick={navigateToSessionPage}
      >
        <div className="relative w-[72px] h-[72px] min-w-[72px] rounded-full overflow-hidden mr-4   border-0">
          <Link
            to={`/${data?.slug}?start=true`}
            className="w-full h-full"
            onClick={handleCompanionClick}
          >
            <SmoothVideo
              src={data?.videoLoopUrl || undefined}
              poster={data?.previewUrl || undefined}
              isOnScreen={isOnScreen}
              className="rounded-xl overflow-hidden"
              loadedMentor={mentorIsReady}
            />
          </Link>
        </div>
        <div className="self-baseline min-w-[68%]">
          <div
            className="tc-mv-avatar-name line-clamp-2 tc-mv-two-lines-text"
            title={currentContext?.title}
            data-testid="avatar-name"
          >
            {currentContext?.title}
          </div>
          <div className=" text-[#F2F2F4] text-sm ">
            {mentorIsReady ? (
              <div className="flex flex-wrap items-center">
                <Link
                  to={`/${data?.slug}?start=true`}
                  onClick={handleCompanionClick}
                >
                  <span className="inline me-2 capitalize">
                    <i className="fa-regular fa-user text-[12px] inline me-1" />
                    {`${data?.firstName} ${data?.lastName}`}
                  </span>
                </Link>
                <span className="bg-dark-neutral-400 w-[4px] h-[4px] rounded-full inline-block me-2" />
                <span className="truncate">
                  <CaseIcon size={14} className="inline me-1" />
                  {data?.jobTitle}
                </span>
              </div>
            ) : (
              <div className="w-48 h-[16px] bg-dark-neutral-400 rounded-full animate-pulse border-0 my-0.5 " />
            )}
          </div>
          {timeAgo && <div className="text-[#F2F2F4] text-sm">{timeAgo}</div>}
        </div>
      </div>
    </div>
  )
})

export { RecentCard }
